"use client";

// Guest invitations: moderators/owners pre-create a validated GUEST account
// and hand over a secret accept-link; the invitee picks username + password.

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiDelete } from "@/lib/api-client";
import type { FamilyGroup } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Mail, Copy, Trash2, CheckCircle2, Clock } from "lucide-react";
import { formatDate } from "@/components/booking/format";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n/provider";

type Invitation = {
  id: string;
  token: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  invitedBy: string;
  familyName: string | null;
  expiresAt: string;
  accepted: boolean;
  expired: boolean;
  createdAt: string;
};

export function InvitationsPanel() {
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", firstName: "", lastName: "", familyId: "" });
  const [lastLink, setLastLink] = useState<{ url: string; email: string } | null>(null);

  const { data: familiesData } = useQuery({
    queryKey: ["families"],
    queryFn: () => apiGet<{ families: FamilyGroup[] }>("/api/admin/families"),
  });
  const families = familiesData?.families ?? [];

  const { data, isLoading } = useQuery({
    queryKey: ["invitations"],
    queryFn: () => apiGet<{ invitations: Invitation[] }>("/api/admin/invitations"),
    refetchInterval: 20000,
  });
  const invites = data?.invitations ?? [];

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await apiPost<{ inviteUrl: string; invitation: { email: string } }>(
        "/api/admin/invitations",
        {
          email: form.email,
          firstName: form.firstName || null,
          lastName: form.lastName || null,
          familyId: form.familyId || null,
        }
      );
      setLastLink({ url: res.inviteUrl, email: res.invitation.email });
      if (navigator.clipboard) navigator.clipboard.writeText(res.inviteUrl).catch(() => {});
      toast.success(t("invitations.sent"));
      setForm({ email: "", firstName: "", lastName: "", familyId: "" });
      qc.invalidateQueries({ queryKey: ["invitations"] });
    } catch (err: any) {
      toast.error(err.message || "Could not send invitation");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    try {
      await apiDelete(`/api/admin/invitations/${id}`);
      toast.success(t("invitations.revoked"));
      qc.invalidateQueries({ queryKey: ["invitations"] });
    } catch (err: any) {
      toast.error(err.message || "Could not revoke");
    }
  };

  const copyLink = async (inv: Invitation) => {
    const url = `${window.location.origin}/invite/${inv.token}`;
    navigator.clipboard.writeText(url).then(() => toast.success(t("invitations.copied")));
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Mail className="h-4 w-4 text-violet-600" /> {t("invitations.inviteGuest")}
          </div>
          <form onSubmit={invite} className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5 sm:col-span-2">
              <Label>{t("invitations.email")}</Label>
              <Input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="guest@example.com"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("invitations.firstName")}</Label>
              <Input
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("invitations.lastName")}</Label>
              <Input
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              />
            </div>
            <div className="grid gap-1.5 sm:col-span-2">
              <Label>{t("groups.assignedTo")}</Label>
              <Select
                value={form.familyId || "none"}
                onValueChange={(v) => setForm((f) => ({ ...f, familyId: v === "none" ? "" : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("groups.unassigned")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("groups.unassigned")}</SelectItem>
                  {families.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                <Mail className="h-4 w-4" /> {t("invitations.send")}
              </Button>
            </div>
          </form>
          {lastLink && (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm dark:border-emerald-900 dark:bg-emerald-950">
              <div className="font-medium text-emerald-800 dark:text-emerald-200">
                {t("invitations.linkReady", { email: lastLink.email })}
              </div>
              <div className="mt-1 flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded bg-white/70 px-2 py-1 font-mono text-xs dark:bg-black/30">
                  {lastLink.url}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => {
                    navigator.clipboard.writeText(lastLink.url).then(() => toast.success(t("invitations.copied")));
                  }}
                >
                  <Copy className="h-3.5 w-3.5" /> {t("invitations.copy")}
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{t("invitations.sendHint")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="mb-3 text-sm font-medium">{t("invitations.history")}</div>
          {isLoading ? (
            <Skeleton className="h-24 w-full rounded-lg" />
          ) : invites.length === 0 ? (
            <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
              {t("invitations.none")}
            </p>
          ) : (
            <div className="space-y-2">
              {invites.map((i) => (
                <div
                  key={i.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{i.email}</span>
                      {i.firstName && (
                        <span className="text-muted-foreground">
                          {[i.firstName, i.lastName].filter(Boolean).join(" ")}
                        </span>
                      )}
                      {i.accepted ? (
                        <Badge className="border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                          <CheckCircle2 className="me-1 h-3 w-3" /> {t("invitations.accepted")}
                        </Badge>
                      ) : i.expired ? (
                        <Badge variant="secondary">{t("invitations.expired")}</Badge>
                      ) : (
                        <Badge
                          className="border-transparent bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                        >
                          <Clock className="me-1 h-3 w-3" /> {t("invitations.pending")}
                        </Badge>
                      )}
                      {i.familyName && (
                        <Badge variant="outline">{i.familyName}</Badge>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {t("invitations.by")} @{i.invitedBy} · {formatDate(i.createdAt, locale)} ·{" "}
                      {t("expenses.dueDate")} {formatDate(i.expiresAt, locale)}
                    </div>
                  </div>
                  {!i.accepted && (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        title={t("invitations.copy")}
                        onClick={() => copyLink(i)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-muted-foreground hover:text-destructive"
                        title={t("invitations.revoke")}
                        onClick={() => revoke(i.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
